import type {
  ClientMessage,
  CaptionSource,
  IceServerConfig,
  MediaState,
  ParticipantSummary,
  ServerMessage,
  TtsVoiceStyle,
} from "../shared/protocol";
import { AudioLevelMonitor, type AudioLevel } from "./audio";

export type ConnectionQuality = {
  grade: "good" | "fair" | "poor" | "waiting";
  rttMs?: number;
  jitterMs?: number;
  lossPercent?: number;
  bitrateKbps?: number;
  route: "direct" | "turn" | "unknown";
  advice: string;
};

export type PeerEvents = {
  onWelcome: (input: {
    selfId: string;
    selfName: string;
    roomId: string;
    isHost: boolean;
    participants: ParticipantSummary[];
  }) => void;
  onParticipantJoined: (participant: ParticipantSummary) => void;
  onParticipantLeft: (participantId: string) => void;
  onRemoteStream: (participantId: string, stream: MediaStream) => void;
  onRemoteLevel: (participantId: string, level: AudioLevel) => void;
  onMediaState: (participantId: string, media: MediaState) => void;
  onCaption: (
    entry: Extract<ServerMessage, { type: "caption" }>,
  ) => void;
  onRecordingState: (
    state: Extract<ServerMessage, { type: "recording-state" }>,
  ) => void;
  onPresentationState: (
    state: Extract<ServerMessage, { type: "presentation-state" }>,
  ) => void;
  onQuality: (quality: ConnectionQuality) => void;
  onNetworkState: (
    state: "connecting" | "connected" | "reconnecting" | "closed",
  ) => void;
  onRoomClosed: (message: string) => void;
  onError: (message: string) => void;
};

type PeerState = {
  participant: ParticipantSummary;
  connection: RTCPeerConnection;
  makingOffer: boolean;
  ignoreOffer: boolean;
  isSettingRemoteAnswerPending: boolean;
  polite: boolean;
  remoteStream: MediaStream;
  pendingCandidates: RTCIceCandidateInit[];
  levelMonitor?: AudioLevelMonitor;
  previousBytes: number;
  previousBytesAt: number;
};

const wait = (duration: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, duration));

export class PeerManager {
  private socket: WebSocket | undefined;
  private selfId = "";
  private roomId = "";
  private isHost = false;
  private iceServers: RTCIceServer[] = [];
  private readonly peers = new Map<string, PeerState>();
  private stopped = false;
  private reconnectAttempt = 0;
  private statsTimer = 0;
  private token = "";
  private connectResolver: (() => void) | undefined;
  private connectRejecter: ((error: Error) => void) | undefined;
  private readonly outgoingStream: MediaStream;

  constructor(
    localStream: MediaStream,
    private readonly events: PeerEvents,
  ) {
    this.outgoingStream = new MediaStream(localStream.getTracks());
  }

  connect(token: string): Promise<void> {
    this.token = token;
    this.stopped = false;
    this.events.onNetworkState("connecting");
    return new Promise((resolve, reject) => {
      this.connectResolver = resolve;
      this.connectRejecter = reject;
      this.openSocket();
    });
  }

  sendMediaState(media: MediaState): void {
    this.send({ type: "media-state", media });
  }

  sendCaption(
    text: string,
    final: boolean,
    sequence: number,
    source: CaptionSource = "speech",
    voiceStyle?: TtsVoiceStyle,
  ): void {
    this.send({
      type: "caption",
      text,
      final,
      sequence,
      source,
      ...(voiceStyle ? { voiceStyle } : {}),
    });
  }

  sendRecordingState(active: boolean, recordingId?: string): void {
    this.send({
      type: "recording-state",
      active,
      ...(recordingId ? { recordingId } : {}),
    });
  }

  sendPresentationState(active: boolean): void {
    this.send({ type: "presentation-state", active });
  }

  getRemoteStreams(): Map<string, MediaStream> {
    return new Map(
      [...this.peers.entries()].map(([id, peer]) => [id, peer.remoteStream]),
    );
  }

  async replaceLocalTrack(
    kind: "audio" | "video",
    track: MediaStreamTrack,
  ): Promise<void> {
    for (const previous of this.outgoingStream
      .getTracks()
      .filter((candidate) => candidate.kind === kind && candidate !== track)) {
      this.outgoingStream.removeTrack(previous);
    }
    if (!this.outgoingStream.getTracks().includes(track)) {
      this.outgoingStream.addTrack(track);
    }
    await Promise.all(
      [...this.peers.values()].map(async (peer) => {
        const sender = peer.connection
          .getSenders()
          .find((candidate) => candidate.track?.kind === kind);
        if (sender) {
          if (sender.track !== track) await sender.replaceTrack(track);
          return;
        }
        const transceiver = peer.connection
          .getTransceivers()
          .find((candidate) => candidate.receiver.track.kind === kind);
        if (transceiver) {
          await transceiver.sender.replaceTrack(track);
          transceiver.direction = "sendrecv";
          return;
        }
        peer.connection.addTrack(track, this.outgoingStream);
      }),
    );
  }

  async setOutgoingVideoTrack(track: MediaStreamTrack | null): Promise<void> {
    for (const previous of this.outgoingStream.getVideoTracks()) {
      if (previous !== track) this.outgoingStream.removeTrack(previous);
    }
    if (track && !this.outgoingStream.getTracks().includes(track)) {
      this.outgoingStream.addTrack(track);
    }
    await Promise.all(
      [...this.peers.values()].map(async (peer) => {
        const transceiver = peer.connection
          .getTransceivers()
          .find((candidate) => candidate.receiver.track.kind === "video");
        if (transceiver) {
          await transceiver.sender.replaceTrack(track);
          if (track && transceiver.direction === "recvonly") {
            transceiver.direction = "sendrecv";
          }
          return;
        }
        if (track) peer.connection.addTrack(track, this.outgoingStream);
      }),
    );
  }

  close(): void {
    this.stopped = true;
    window.clearInterval(this.statsTimer);
    this.socket?.close(1000, "leaving");
    this.socket = undefined;
    this.closePeers();
    this.events.onNetworkState("closed");
  }

  private openSocket(): void {
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    const websocket = new WebSocket(
      `${scheme}//${location.host}/ws?token=${encodeURIComponent(this.token)}`,
    );
    this.socket = websocket;

    websocket.addEventListener("open", () => {
      this.reconnectAttempt = 0;
    });
    websocket.addEventListener("message", (event) => {
      this.onMessage(String(event.data));
    });
    websocket.addEventListener("error", () => {
      if (this.connectRejecter) {
        this.connectRejecter(
          new Error("회의 서버에 연결하지 못했습니다."),
        );
        this.connectRejecter = undefined;
      }
    });
    websocket.addEventListener("close", (event) => {
      if (this.stopped) return;
      if ([4002, 4003, 4004].includes(event.code)) {
        const message =
          event.code === 4003
            ? "호스트가 입장한 뒤 회의에 참여할 수 있습니다."
            : event.code === 4004
              ? "호스트가 퇴장해 회의가 종료되었습니다."
              : "회의실이 닫혔거나 만료되었습니다.";
        this.stopped = true;
        window.clearInterval(this.statsTimer);
        this.closePeers();
        this.events.onNetworkState("closed");
        this.connectRejecter?.(new Error(message));
        this.connectRejecter = undefined;
        this.connectResolver = undefined;
        this.events.onRoomClosed(event.reason || message);
        return;
      }
      if (event.code === 4008) {
        this.events.onError("회의실 정원이 가득 찼습니다.");
        this.connectRejecter?.(new Error("회의실 정원이 가득 찼습니다."));
        this.stopped = true;
        return;
      }
      void this.reconnect();
    });
  }

  private async reconnect(): Promise<void> {
    this.events.onNetworkState("reconnecting");
    this.closePeers();
    this.reconnectAttempt += 1;
    if (this.reconnectAttempt > 8) {
      this.events.onError("회의 연결을 복구하지 못했습니다. 다시 입장하세요.");
      this.events.onNetworkState("closed");
      return;
    }
    await wait(Math.min(10_000, 700 * 2 ** (this.reconnectAttempt - 1)));
    if (!this.stopped) this.openSocket();
  }

  private onMessage(raw: string): void {
    let message: ServerMessage;
    try {
      message = JSON.parse(raw) as ServerMessage;
    } catch {
      this.events.onError("서버 메시지를 읽을 수 없습니다.");
      return;
    }

    if (message.type === "welcome") {
      this.selfId = message.selfId;
      this.roomId = message.roomId;
      this.isHost = message.isHost;
      this.iceServers = message.iceServers.map(
        (server: IceServerConfig): RTCIceServer => ({
          urls: server.urls,
          ...(server.username ? { username: server.username } : {}),
          ...(server.credential ? { credential: server.credential } : {}),
        }),
      );
      if (
        message.presentation &&
        message.presentation.presenterId !== this.selfId
      ) {
        for (const track of this.outgoingStream.getVideoTracks()) {
          this.outgoingStream.removeTrack(track);
        }
      }
      this.closePeers();
      for (const participant of message.participants) {
        this.createPeer(participant);
      }
      this.events.onWelcome({
        selfId: this.selfId,
        selfName: message.selfName,
        roomId: this.roomId,
        isHost: this.isHost,
        participants: message.participants,
      });
      if (message.presentation) {
        this.events.onPresentationState({
          type: "presentation-state",
          active: true,
          ...message.presentation,
        });
      } else {
        this.events.onPresentationState({
          type: "presentation-state",
          active: false,
        });
      }
      this.events.onNetworkState("connected");
      this.connectResolver?.();
      this.connectResolver = undefined;
      this.connectRejecter = undefined;
      window.clearInterval(this.statsTimer);
      this.statsTimer = window.setInterval(() => {
        void this.collectQuality();
      }, 1_000);
      return;
    }

    if (message.type === "participant-joined") {
      this.createPeer(message.participant);
      this.events.onParticipantJoined(message.participant);
      return;
    }

    if (message.type === "participant-left") {
      this.removePeer(message.participantId);
      this.events.onParticipantLeft(message.participantId);
      return;
    }

    if (message.type === "signal") {
      void this.handleSignal(message);
      return;
    }

    if (message.type === "media-state") {
      const peer = this.peers.get(message.participantId);
      if (peer) peer.participant.media = message.media;
      this.events.onMediaState(message.participantId, message.media);
      return;
    }

    if (message.type === "caption") {
      this.events.onCaption(message);
      return;
    }

    if (message.type === "recording-state") {
      this.events.onRecordingState(message);
      return;
    }

    if (message.type === "presentation-state") {
      this.events.onPresentationState(message);
      return;
    }

    if (message.type === "error") {
      this.events.onError(message.message);
    }
  }

  private createPeer(participant: ParticipantSummary): PeerState {
    const existing = this.peers.get(participant.id);
    if (existing) return existing;

    const connection = new RTCPeerConnection({
      iceServers: this.iceServers,
      bundlePolicy: "max-bundle",
    });
    const peer: PeerState = {
      participant,
      connection,
      makingOffer: false,
      ignoreOffer: false,
      isSettingRemoteAnswerPending: false,
      polite: this.selfId.localeCompare(participant.id) > 0,
      remoteStream: new MediaStream(),
      pendingCandidates: [],
      previousBytes: 0,
      previousBytesAt: performance.now(),
    };
    this.peers.set(participant.id, peer);

    const localKinds = new Set(this.outgoingStream.getTracks().map((track) => track.kind));
    for (const track of this.outgoingStream.getTracks()) {
      const sender = connection.addTrack(track, this.outgoingStream);
      if (track.kind === "video") {
        const parameters = sender.getParameters();
        parameters.encodings =
          parameters.encodings.length > 0 ? parameters.encodings : [{}];
        const encoding = parameters.encodings[0];
        if (encoding) {
          encoding.maxBitrate = 1_600_000;
          encoding.maxFramerate = 24;
        }
        parameters.degradationPreference = "maintain-framerate";
        void sender.setParameters(parameters).catch((error: unknown) => {
          console.warn("Video bitrate limit could not be applied", error);
        });
      }
    }
    if (!localKinds.has("audio")) {
      connection.addTransceiver("audio", { direction: "recvonly" });
    }
    if (!localKinds.has("video")) {
      connection.addTransceiver("video", { direction: "recvonly" });
    }

    connection.onicecandidate = ({ candidate }) => {
      this.send({
        type: "signal",
        to: participant.id,
        candidate: candidate
          ? {
              candidate: candidate.candidate,
              sdpMid: candidate.sdpMid,
              sdpMLineIndex: candidate.sdpMLineIndex,
              usernameFragment: candidate.usernameFragment,
            }
          : null,
      });
    };

    connection.ontrack = ({ track, streams }) => {
      const stream = streams[0];
      if (stream) {
        peer.remoteStream = stream;
      } else if (!peer.remoteStream.getTracks().includes(track)) {
        peer.remoteStream.addTrack(track);
      }
      this.events.onRemoteStream(participant.id, peer.remoteStream);
      if (track.kind === "audio" && !peer.levelMonitor) {
        peer.levelMonitor = new AudioLevelMonitor(
          new MediaStream([track]),
          (level) => this.events.onRemoteLevel(participant.id, level),
        );
      }
    };

    connection.onnegotiationneeded = async () => {
      // 최초 연결에서는 ID로 정한 한쪽만 제안해 양쪽 동시 offer와 ICE 롤백을 피합니다.
      // 원격 설명이 생긴 뒤의 장치 추가·변경 재협상은 양쪽 모두 허용합니다.
      if (peer.polite && !connection.remoteDescription) return;
      try {
        peer.makingOffer = true;
        await connection.setLocalDescription();
        if (connection.localDescription) {
          this.send({
            type: "signal",
            to: participant.id,
            description: connection.localDescription.toJSON(),
          });
        }
      } catch (error) {
        console.error("Negotiation failed", error);
      } finally {
        peer.makingOffer = false;
      }
    };

    connection.onconnectionstatechange = () => {
      if (connection.connectionState === "failed") {
        connection.restartIce();
      }
    };
    return peer;
  }

  private async handleSignal(
    message: Extract<ServerMessage, { type: "signal" }>,
  ): Promise<void> {
    const peer = this.peers.get(message.from);
    if (!peer) return;
    const connection = peer.connection;
    try {
      if (message.description) {
        const rtcDescription: RTCSessionDescriptionInit =
          message.description.sdp === undefined
            ? { type: message.description.type }
            : {
                type: message.description.type,
                sdp: message.description.sdp,
              };
        const readyForOffer =
          !peer.makingOffer &&
          (connection.signalingState === "stable" ||
            peer.isSettingRemoteAnswerPending);
        const offerCollision =
          message.description.type === "offer" && !readyForOffer;
        peer.ignoreOffer = !peer.polite && offerCollision;
        if (peer.ignoreOffer) return;

        peer.isSettingRemoteAnswerPending =
          message.description.type === "answer";
        try {
          await connection.setRemoteDescription(rtcDescription);
        } finally {
          peer.isSettingRemoteAnswerPending = false;
        }
        for (const candidate of peer.pendingCandidates.splice(0)) {
          await connection.addIceCandidate(candidate);
        }
        if (message.description.type === "offer") {
          await connection.setLocalDescription();
          if (connection.localDescription) {
            this.send({
              type: "signal",
              to: message.from,
              description: connection.localDescription.toJSON(),
            });
          }
        }
      } else if ("candidate" in message && message.candidate) {
        if (peer.ignoreOffer) return;
        const rtcCandidate: RTCIceCandidateInit = {
          candidate: message.candidate.candidate,
          sdpMid: message.candidate.sdpMid ?? null,
          sdpMLineIndex: message.candidate.sdpMLineIndex ?? null,
          usernameFragment: message.candidate.usernameFragment ?? null,
        };
        try {
          if (connection.remoteDescription) {
            await connection.addIceCandidate(rtcCandidate);
          } else {
            peer.pendingCandidates.push(rtcCandidate);
          }
        } catch (error) {
          if (!peer.ignoreOffer) throw error;
        }
      }
    } catch (error) {
      console.error("Signal handling failed", error);
    }
  }

  private async collectQuality(): Promise<void> {
    if (this.peers.size === 0) {
      this.events.onQuality({
        grade: "waiting",
        route: "unknown",
        advice: "참가자가 연결되면 네트워크 품질을 진단합니다.",
      });
      return;
    }

    let packetsLost = 0;
    let packetsReceived = 0;
    let totalBytes = 0;
    let maxJitter = 0;
    let maxRtt = 0;
    const routeFlags = { turn: false, pair: false };
    const now = performance.now();
    let bitrateKbps = 0;

    for (const peer of this.peers.values()) {
      const report = await peer.connection.getStats();
      let peerBytes = 0;
      report.forEach((stat) => {
        if (
          stat.type === "inbound-rtp" &&
          !stat.isRemote &&
          (stat.kind === "audio" || stat.mediaType === "audio")
        ) {
          packetsLost += Number(stat.packetsLost ?? 0);
          packetsReceived += Number(stat.packetsReceived ?? 0);
          maxJitter = Math.max(maxJitter, Number(stat.jitter ?? 0) * 1000);
          peerBytes += Number(stat.bytesReceived ?? 0);
        }
        if (
          stat.type === "candidate-pair" &&
          (stat.nominated || stat.selected) &&
          stat.state === "succeeded"
        ) {
          maxRtt = Math.max(
            maxRtt,
            Number(stat.currentRoundTripTime ?? 0) * 1000,
          );
          const local = report.get(stat.localCandidateId);
          const remote = report.get(stat.remoteCandidateId);
          routeFlags.pair = true;
          routeFlags.turn ||= Boolean(
            local?.candidateType === "relay" ||
              remote?.candidateType === "relay",
          );
        }
      });
      const elapsedSeconds = Math.max(
        0.1,
        (now - peer.previousBytesAt) / 1000,
      );
      if (peer.previousBytes > 0) {
        bitrateKbps +=
          ((peerBytes - peer.previousBytes) * 8) / elapsedSeconds / 1000;
      }
      peer.previousBytes = peerBytes;
      peer.previousBytesAt = now;
      totalBytes += peerBytes;
    }

    const totalPackets = packetsReceived + Math.max(0, packetsLost);
    const lossPercent =
      totalPackets > 0 ? (Math.max(0, packetsLost) / totalPackets) * 100 : 0;
    const grade: ConnectionQuality["grade"] =
      lossPercent >= 5 || maxJitter >= 50 || maxRtt >= 300
        ? "poor"
        : lossPercent >= 2 || maxJitter >= 30 || maxRtt >= 150
          ? "fair"
          : "good";
    const route: ConnectionQuality["route"] = routeFlags.turn
      ? "turn"
      : routeFlags.pair
        ? "direct"
        : "unknown";
    const advice =
      grade === "poor"
        ? "네트워크가 불안정합니다. 유선 연결이나 가까운 Wi-Fi를 사용하세요."
        : grade === "fair"
          ? "연결 변동이 감지됩니다. 다른 대용량 전송을 잠시 멈춰 보세요."
          : route === "turn"
            ? "자체 TURN 릴레이로 안정적으로 연결되어 있습니다."
            : "브라우저끼리 직접 연결되어 있습니다.";

    void totalBytes;
    this.events.onQuality({
      grade,
      rttMs: Math.round(maxRtt),
      jitterMs: Math.round(maxJitter),
      lossPercent: Number(lossPercent.toFixed(1)),
      bitrateKbps: Math.max(0, Math.round(bitrateKbps)),
      route,
      advice,
    });
  }

  private removePeer(participantId: string): void {
    const peer = this.peers.get(participantId);
    if (!peer) return;
    peer.levelMonitor?.stop();
    peer.connection.close();
    this.peers.delete(participantId);
  }

  private closePeers(): void {
    for (const id of [...this.peers.keys()]) this.removePeer(id);
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }
}
